import { IconButton, Text, Center, Spinner } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { ArrowRightFromLine } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import pickBy from 'lodash-es/pickBy.js';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { emptyPaginationResponse, SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { ProviderCauseTable } from './ProviderCausesTable';
import { ProviderCausesDatesFilter } from './ProviderCausesDatesFilter';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';

export interface SortFilters {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page: string | undefined;
  dateRange: string | undefined;
}

export function ProviderCausesLayout() {
  const { currentPage, pageSize, setPage } = usePagination();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['createdAt'],
    sort: [SortOrder.DESC],
  });

  const querySearch = searchParams.get('search') ?? '';

  const URLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, search, orderBy, sort, dateRange, ...rest } = urlFilters;

    return {
      ...rest,
      orderBy: orderBy ?? 'createdAt',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
      page: page ?? '1',
      dateRange: dateRange ?? undefined,
    };
  }, [searchParams]);

  const { data: providers } = trpcAdmin.providers.all.useQuery({});
  const { data: causes, isFetching } =
    trpcAdmin.finances.providerCausesReport.listProviderCauses.useQuery(
      {
        search: querySearch.trim(),
        filters: URLFilters,
        pagination: {
          page: Number(currentPage),
          limit: pageSize,
        },
      },
      {
        initialData: emptyPaginationResponse(),
        placeholderData: (previousData) => previousData,
      },
    );

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const getProviderName = useCallback(
    (providerDrn: string): string => {
      if (Array.isArray(providers) && providerDrn) {
        const provider = providers.find(({ drn }) => drn === providerDrn);
        if (provider) {
          return provider.name || '';
        }
      }
      return '';
    },
    [providers],
  );

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded && (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ),
        <PageHeading key="page-heading">
          <Trans>Provider Causes</Trans>
        </PageHeading>,
      ]}
      filters={[
        <>
          <SearchInput maxW="search-input" />
          <ProviderCausesDatesFilter key="date-range" />
        </>,
      ]}
      table={
        isFetching ? (
          <Center p={8}>
            <Spinner size="xl" transitionDelay={'2900ms'} />
          </Center>
        ) : causes != null && Array.isArray(causes.items) && causes.items.length > 0 ? (
          <ProviderCauseTable
            causes={causes.items}
            getProviderName={getProviderName}
            urlParams={URLFilters}
            onFilters={handleFilterChange}
          />
        ) : (
          <Center p={8}>
            <Text textAlign="center">
              <Trans>No causes found</Trans>
            </Text>
          </Center>
        )
      }
      pagination={
        Array.isArray(causes?.items) && causes.items.length > 0 ? (
          <Pagination pagination={causes.pagination} onPageChange={setPage} />
        ) : null
      }
    />
  );
}
